
import { Exam } from './types';
import { generateExamReport } from './generator';
import { NALC_LOGO_URL } from '../printStyles';
import { toast } from 'sonner';
import { getBrowserInfo } from '../printingBridge';
import { isDesktopEnvironment } from '../../desktop/desktopEnvironment';
import { dbService } from '@/services/db';

// Enhanced print function with better error handling and data mapping
export const printStudentExamReport = async (studentId: number | string, examResults: any[]): Promise<void> => {
  try {
    // Show toast notification that we're generating the report
    toast.info("Generating exam certificate...");
    
    // Log environment info
    const browserInfo = getBrowserInfo();
    console.log('Print environment details:', browserInfo);
    
    // Use the dbService directly to get student data
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student) {
      console.error(`Student with ID ${studentId} not found`);
      toast.error(`Student with ID ${studentId} not found`);
      return Promise.reject(new Error(`Student with ID ${studentId} not found`));
    }
    
    // If no exam results are provided, there's nothing to print
    if (!examResults || examResults.length === 0) {
      console.error('No exam results to print');
      toast.error('No exam results to print');
      return Promise.reject(new Error('No exam results to print'));
    }
    
    // Select the most recent exam to print
    const examToPrint = examResults[0]; // Default to first exam
    
    console.log('Exam to print:', examToPrint); // Debug log to see what data we're getting
    
    // Map the ExamResult to the Exam interface expected by generateExamReport
    // Ensure proper numeric conversion of scores to prevent 0 values
    const exam: Exam = {
      title: examToPrint.subject || 'Exam',
      date: examToPrint.examDate || new Date().toISOString(),
      totalMarks: Number(examToPrint.maxScore) || 100,
      marksObtained: Number(examToPrint.obtainedScore) || 0,
      subject: examToPrint.subject || '',
      remarks: examToPrint.remarks || '',
      examType: examToPrint.examType || 'test'
    };
    
    console.log('Formatted exam for printing:', exam); // Debug log
    
    // Convert DB student to the format expected by generateExamReport
    const formattedStudent = {
      ...student,
      id: student.id?.toString(), // Convert the ID to string
      name: student.name || ''
    };
    
    // Use different settings for desktop environments
    const isDesktopApp = isDesktopEnvironment();
    const isNativefier = browserInfo.isNativefier;
    
    if (isDesktopApp || isNativefier) {
      console.log('Using desktop-specific settings for exam report');
      
      // Desktop environments need to open in system browser
      toast.info("Opening certificate in external browser...", { duration: 3000 });
      
      // Generate and save the PDF with desktop-specific settings
      generateExamReport(exam, formattedStudent, NALC_LOGO_URL, { 
        useExternalBrowser: true,
        delay: 1200
      });
    } else {
      // Generate and save the PDF with standard settings
      generateExamReport(exam, formattedStudent, NALC_LOGO_URL);
    }
    
    toast.success("Exam certificate generated successfully");
    return Promise.resolve();
  } catch (error) {
    console.error('Error printing exam report:', error);
    toast.error('Failed to generate exam certificate');
    return Promise.reject(error);
  }
};

// Function to print a specific exam certificate by exam ID
export const printSpecificExamCertificate = async (studentId: number | string, examId: string): Promise<void> => {
  try {
    toast.info("Generating specific exam certificate...");
    
    // Log environment info
    const browserInfo = getBrowserInfo();
    console.log('Print environment for specific certificate:', browserInfo);
    
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student || !student.examResults) {
      toast.error(`Student data or exam results not found`);
      return Promise.reject(new Error('Student data or exam results not found'));
    }
    
    // Find the specific exam by ID
    const examResult = student.examResults.find(exam => exam.id === examId);
    
    if (!examResult) {
      toast.error(`Exam with ID ${examId} not found`);
      return Promise.reject(new Error(`Exam with ID ${examId} not found`));
    }
    
    console.log('Found specific exam to print:', examResult); // Debug log
    
    // Desktop-specific handling
    if (isDesktopEnvironment() || browserInfo.isNativefier) {
      console.log('Using desktop-specific settings for specific exam certificate');
      toast.info("Opening certificate in external browser...", { duration: 3000 });
    }
    
    // Call the print function with just this exam
    await printStudentExamReport(studentId, [examResult]);
    
    return Promise.resolve();
  } catch (error) {
    console.error('Error printing specific exam certificate:', error);
    toast.error('Failed to generate specific exam certificate');
    return Promise.reject(error);
  }
};
